import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { reverse, uniq, uniqBy } from 'lodash'
import railsApiService from 'services/api/rails-api-service'
import { Consultation, ConsultationMessage } from 'types'
import { mergeConsultationSnapshot, mergeConsultationWithoutLiveRegression } from 'utils/consultationRealtime'

export interface ConsultationsState {
  isLoading: boolean
  consultations: Consultation[]
  messages: ConsultationMessage[]
  showPatientNotificationMessage: boolean
  realtimeRevision: number
  realtimeRevisionByConsultation: Record<number, number>
  requestRealtimeRevisions: Record<string, number>
}

const initialState: ConsultationsState = {
  isLoading: false,
  consultations: [],
  messages: [],
  showPatientNotificationMessage: false,
  realtimeRevision: 0,
  realtimeRevisionByConsultation: {},
  requestRealtimeRevisions: {}
}

export const consultationsSlice = createSlice({
  name: 'consultations',
  initialState,
  reducers: {
    resetConsultationsState: (state) => {
      state = initialState
      return state
    },
    updateConsultation: (state, action: PayloadAction<Consultation | null>) => {
      if (!action.payload) return
      state.realtimeRevision += 1
      state.realtimeRevisionByConsultation[action.payload.id] = state.realtimeRevision
      let updated = false
      state.consultations = state.consultations.map((consultation) => {
        if (consultation.id === action.payload?.id) {
          updated = true
          return mergeConsultationWithoutLiveRegression(consultation, action.payload)
        }
        return consultation
      })
      // ActionCable may beat the initial GET. Dropping that first started event
      // would let the older waiting snapshot win permanently.
      if (!updated) state.consultations.push(action.payload)
    },
    appendMessage: (state, action: PayloadAction<ConsultationMessage>) => {
      state.messages = uniqBy([action.payload, ...state.messages], 'id')
    },
    setPatientNotificationMessage: (state, action: PayloadAction<boolean>) => {
      state.showPatientNotificationMessage = action.payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getConsultations.pending, (state, action) => {
        state.isLoading = true
        state.requestRealtimeRevisions[action.meta.requestId] = state.realtimeRevision
      })
      .addCase(getConsultations.fulfilled, (state, action) => {
        state.isLoading = false
        const requestRevision = state.requestRealtimeRevisions[action.meta.requestId]
        const currentById = new Map(state.consultations.map((consultation) => [consultation.id, consultation]))
        const snapshot = mergeConsultationSnapshot(state.consultations, action.payload)

        // If ActionCable updated a consultation after this request began, its
        // response is allowed to be older than that event. Preserve only those
        // event-owned records; other records still refresh normally.
        const reconciledSnapshot = snapshot.map((consultation) => {
          const eventRevision = state.realtimeRevisionByConsultation[consultation.id]
          return requestRevision !== undefined && eventRevision > requestRevision
            ? currentById.get(consultation.id) ?? consultation
            : consultation
        })
        const reconciledIds = new Set(reconciledSnapshot.map((consultation) => consultation.id))
        const eventsMissingFromSnapshot = state.consultations.filter((consultation) => {
          const eventRevision = state.realtimeRevisionByConsultation[consultation.id]
          return requestRevision !== undefined && eventRevision > requestRevision && !reconciledIds.has(consultation.id)
        })
        state.consultations = [...reconciledSnapshot, ...eventsMissingFromSnapshot]
        delete state.requestRealtimeRevisions[action.meta.requestId]
      })
      .addCase(getConsultations.rejected, (state, action) => {
        state.isLoading = false
        delete state.requestRealtimeRevisions[action.meta.requestId]
      })
      .addCase(getMessages.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        state.isLoading = false
        state.messages = uniqBy([...action.payload, ...state.messages], 'id')
      })
      .addCase(getMessages.rejected, (state) => {
        state.isLoading = false
      })
})

export const { resetConsultationsState, updateConsultation, appendMessage, setPatientNotificationMessage } =
  consultationsSlice.actions

export default consultationsSlice.reducer

export const getConsultations = createAsyncThunk('get-consultations', async (_, { rejectWithValue }) => {
  try {
    const { data } = await railsApiService.apiInstance.get<Consultation[]>('/api/v1/consultations')

    return data
  } catch (error) {
    return rejectWithValue(error)
  }
})

interface GetMessagesParams {
  user_id: number
}

export const getMessages = createAsyncThunk(
  'get-messages',
  async ({ user_id }: GetMessagesParams, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.get<ConsultationMessage[]>('/api/v1/messages', {
        params: {
          user_id
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface CreateMessageParams {
  user_id: number
  body: string
}

export const createMessage = createAsyncThunk(
  'create-message',
  async ({ user_id, body }: CreateMessageParams, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await railsApiService.apiInstance.post<ConsultationMessage>('/api/v1/messages', {
        message: {
          body,
          user_id
        }
      })

      dispatch(appendMessage(data))

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
